"use client";
import React, { useState, useEffect } from 'react';
import MultiStepLeadForm from '@/app/components/LeadForm';
import { CheckCircle, Phone, Clock, Shield, DollarSign, Users, Zap, Award, Star, TrendingUp, AlertCircle, X, MapPin, Building, Store } from 'lucide-react';

export default function TampaLandingPage() {
  const [showUrgencyBanner, setShowUrgencyBanner] = useState(true);
  const [timeLeft, setTimeLeft] = useState(3600);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds: any) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const testimonials = [
    {
      text: "Went from paying $1247/month to ZERO fees. MidaPOS literally saved my business!",
      author: "Maria Rodriguez",
      business: "Convenience Store - South Tampa",
      savings: "$847/mo SAVED"
    },
    {
      text: "Called at 2PM, operating by 5PM same day. Already processed $23,000 with ZERO fees!",
      author: "Ahmed Hassan",
      business: "Tampa Smokes & Vape - Temple Terrace ",
      savings: "SAME DAY PROFIT"
    },
    {
      text: "Ditched Square's $712/month robbery. Now customers pay the fees, not me!",
      author: "Virginia Kim",
      business: "ALterations Inc - Downtown Tampa",
      savings: "$312/mo SAVED"
    }
  ];

  const tampaAreas = [
    { area: "Tampa", type: "FREE – POS System and 0% Processing Fee", icon: "🏢", highlight: true },
    { area: "Saint Petersburg", type: "FREE – POS System and 0% Processing Fee", icon: "🏖️", highlight: true },
    { area: "Lakeland", type: "FREE – POS System and 0% Processing Fee", icon: "🌊", highlight: true },
    { area: "Brandon", type: "FREE – POS System and 0% Processing Fee", icon: "🏘️", highlight: true },
    { area: "Ybor City", type: "FREE – POS System and 0% Processing Fee", icon: "🎭", highlight: true },
    { area: "South Tampa", type: "FREE – POS System and 0% Processing Fee", icon: "🏡", highlight: true },
    { area: "Westshore", type: "FREE – POS System and 0% Processing Fee", icon: "🏢", highlight: true },
    { area: "Downtown Tampa", type: "FREE – POS System and 0% Processing Fee", icon: "🌆", highlight: true },
    { area: "Channelside", type: "FREE – POS System and 0% Processing Fee", icon: "🚢", highlight: true },
    { area: "Seminole Heights", type: "FREE – POS System and 0% Processing Fee", icon: "🌳", highlight: true },
    { area: "Temple Terrace", type: "FREE – POS System and 0% Processing Fee", icon: "🏫", highlight: true },
    { area: "Riverview", type: "FREE – POS System and 0% Processing Fee", icon: "🌊", highlight: true },
    { area: "Gulfport", type: "FREE – POS System and 0% Processing Fee", icon: "⛵", highlight: true },
    { area: "Pinellas Park", type: "FREE – POS System and 0% Processing Fee", icon: "🏞️", highlight: true },
    { area: "East Tampa", type: "FREE – POS System and 0% Processing Fee", icon: "🌅", highlight: true },
    { area: "Palm River-Clair Mel", type: "FREE – POS System and 0% Processing Fee", icon: "🌴", highlight: true },
    { area: "University Area", type: "FREE – POS System and 0% Processing Fee", icon: "🎓", highlight: true }
  ];

  const businessTypes = [
    { type: "Convenience Store", areas: ["Tampa Bay", "South Tampa", "East Tampa"], icon: "🏪" },
    { type: "Smoke Shop", areas: ["Tampa", "Ybor City", "Brandon"], icon: "💨" },
    { type: "Nail Salon", areas: ["Tampa", "Saint Petersburg", "Westshore"], icon: "💅" },
    { type: "Restaurant", areas: ["Downtown Tampa", "Channelside", "Hyde Park"], icon: "🍽️" },
    { type: "Retail Store", areas: ["Tampa", "Lakeland", "Temple Terrace"], icon: "🛍️" },
    { type: "Auto Shop", areas: ["Brandon", "Riverview", "Plant City"], icon: "🔧" },
    { type: "Beauty Salon", areas: ["South Tampa", "Seminole Heights", "Carrollwood"], icon: "✂️" },
    { type: "Gas Station", areas: ["Tampa", "Saint Petersburg", "Clearwater"], icon: "⛽" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <style jsx>{`
        .area-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1rem;
        }
        .floating-badge {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .glow-effect {
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
        }
      `}</style>

      {/* Urgency Banner */}
      {showUrgencyBanner && (
        <div className="bg-red-600 text-white px-4 py-3 relative">
          <div className="flex items-center justify-center space-x-4 text-center">
            <AlertCircle className="w-5 h-5 animate-pulse" />
            <span className="font-bold">TAMPA FLASH SALE: FREE Terminal + Zero Fees Forever Ends in {formatTime(timeLeft)}</span>
            <AlertCircle className="w-5 h-5 animate-pulse" />
          </div>
          <button
            onClick={() => setShowUrgencyBanner(false)}
            aria-label="Close urgency banner"
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-red-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/locations/tampa.webp"
            alt="Tampa skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/20 backdrop-blur-sm"></div>
        </div>
        {/* <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900"></div>
        <div className="absolute inset-0 bg-black opacity-20"></div> */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-orange-500 rounded-full opacity-10 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-blue-400 rounded-full opacity-10 animate-bounce"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            {/* Content - Takes 2/3 on desktop */}
            <div className="lg:col-span-2 space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold animate-pulse">
                  <Star className="w-4 h-4" />
                  <span>TAMPA'S #1 CASH DISCOUNT SPECIALIST</span>
                </div>

                <h1 className="text-4xl lg:text-6xl font-black leading-tight">
                  TAMPA BUSINESSES:
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
                    Turn Credit Fees Into Profit
                  </span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500">
                    (Free Terminal)
                  </span>
                </h1>

                <h2 className="text-xl lg:text-2xl font-bold text-orange-300">
                  Turn Every Credit Card Into CASH • FREE $1379 Terminal • Setup TODAY
                </h2>
              </div>

              {/* Benefits */}
              <div className="space-y-3">
                {[
                  "Pocket $300-$1,000+ Every Month (Guaranteed)",
                  "FREE $1379 Terminal (ONLY 12 Left in Tampa)",
                  "Operating & Profitable in 90 Minutes"
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-lg font-semibold">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Social Proof Numbers */}
              <div className="grid grid-cols-3 gap-6 py-6 border-t border-b border-blue-400 border-opacity-30">
                <div className="text-center">
                  <div className="text-3xl font-black text-orange-400">1,247+</div>
                  <div className="text-sm text-blue-200">Tampa Businesses</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-green-400">$8.7M</div>
                  <div className="text-sm text-blue-200">Saved in 2025</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-yellow-400">73 MIN</div>
                  <div className="text-sm text-blue-200">Avg Setup</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-4">
                <button
                  onClick={() => document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-4 rounded-xl text-xl font-black transition-all duration-300 transform hover:scale-105 shadow-2xl animate-pulse"
                >
                  🔥 ELIMINATE MY FEES + CLAIM FREE TERMINAL
                </button>

                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <a href="tel:3053067774" className="flex items-center space-x-2 bg-white bg-opacity-10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-900 px-6 py-3 rounded-xl text-lg font-bold transition-all duration-300">
                    <Phone className="w-5 h-5" />
                    <span>TAMPA HOTLINE: (305) 306-7774</span>
                  </a>
                  <div className="text-sm text-blue-200">⚡ Local Tampa Rep Answers</div>
                </div>
              </div>

              {/* Risk Reversal */}
              <div className="bg-green-600 bg-opacity-20 backdrop-blur-sm border border-green-400 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-green-300">
                  <Shield className="w-5 h-5" />
                  <span className="font-bold">IRON-CLAD TAMPA GUARANTEE:</span>
                </div>
                <p className="text-green-100 mt-1">60-day money-back guarantee + cancel anytime + no hidden fees EVER.</p>
              </div>
            </div>

            {/* Lead Form - Takes 1/3 on desktop, wider and more visible */}
            <div className="lg:col-span-1 w-full mx-auto lg:w-[480px]">
              <div id="lead-form" className="bg-white shadow-2xl rounded-2xl p-8 border-4 border-orange-400 sticky top-4 w-full">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-black text-gray-900 mb-2">
                    ✅ Tampa Limited Offer
                  </h3>
                  <p className="text-orange-700 font-bold">💥 Only 12 Free Terminals Left — Act Fast!</p>
                </div>
                <MultiStepLeadForm mode="quick" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rotating Testimonial Banner */}
      <section className="bg-gradient-to-r from-black to-gray-900 text-white py-4">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-center space-x-8 animate-pulse">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 font-bold">TAMPA BUSINESSES WINNING:</span>
            </div>
            <div className="text-lg font-semibold">
              "{testimonials[currentTestimonial].text}" - {testimonials[currentTestimonial].author}
            </div>
            <div className="bg-green-500 text-black px-3 py-1 rounded-full text-sm font-bold">
              POCKETS {testimonials[currentTestimonial].savings}
            </div>
          </div>
        </div>
      </section>

      {/* Tampa Bay Area Coverage SEO Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-full text-lg font-bold mb-6 floating-badge">
              <MapPin className="w-6 h-6" />
              <span>TAMPA BAY AREA DOMINANCE</span>
            </div>
            <h2 className="text-5xl font-black text-blue-900 mb-6">
              FREE POS Terminals Across ALL Tampa Bay Communities
            </h2>
            <p className="text-2xl text-blue-800 font-bold max-w-4xl mx-auto">
              From Downtown Tampa to Saint Petersburg, Lakeland to Brandon - we're eliminating processing fees for EVERY neighborhood business
            </p>
          </div>

          {/* Main Tampa Bay Areas Grid */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-center text-blue-900 mb-8">🎯 MAJOR TAMPA BAY COVERAGE AREAS</h3>
            <div className="area-grid">
              {tampaAreas.map((location, index) => (
                <div
                  key={index}
                  className="relative bg-white border-2 border-blue-200 rounded-xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <div className="text-4xl mb-3">{location.icon}</div>
                  {location.highlight && (
                    <CheckCircle className="absolute top-3 right-3 text-green-500 w-6 h-6" />
                  )}
                  <h4 className="text-xl font-bold mb-2 text-blue-900">
                    {location.area}
                  </h4>
                  <div className="text-sm text-blue-700">
                    {location.type}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Business Type Specific Coverage */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-center text-blue-900 mb-8">🏪 SPECIALIZED POS SOLUTIONS BY BUSINESS TYPE</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {businessTypes.map((business, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500 hover:shadow-2xl transition-all duration-300 hover:transform hover:scale-105">
                  <div className="text-center">
                    <div className="text-3xl mb-3">{business.icon}</div>
                    <h4 className="text-lg font-bold text-blue-900 mb-3">{business.type} POS</h4>
                    <div className="space-y-1 text-sm text-blue-700">
                      {business.areas.map((area, i) => (
                        <div key={i} className="bg-blue-50 rounded px-2 py-1">📍 {area}</div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Extended Tampa Coverage */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white rounded-2xl p-8 mb-12">
            <h3 className="text-3xl font-bold text-center mb-8">🌟 COMPLETE TAMPA BAY METRO COVERAGE</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-xl font-bold mb-4 text-yellow-300">🏢 URBAN TAMPA CORE</h4>
                <div className="space-y-2 text-blue-100">
                  <div>• Downtown Tampa POS System</div>
                  <div>• Ybor City POS Terminal</div>
                  <div>• Channelside POS Services</div>
                  <div>• Westshore POS Solutions</div>
                  <div>• Hyde Park POS System</div>
                  <div>• Davis Islands POS</div>
                </div>
              </div>
              <div>
                <h4 className="text-xl font-bold mb-4 text-yellow-300">🏘️ SUBURBAN COMMUNITIES</h4>
                <div className="space-y-2 text-blue-100">
                  <div>• South Tampa POS System</div>
                  <div>• Brandon POS Terminal</div>
                  <div>• Temple Terrace POS</div>
                  <div>• Seminole Heights POS</div>
                  <div>• Carrollwood POS System</div>
                  <div>• Town 'n' Country POS</div>
                </div>
              </div>
              <div>
                <h4 className="text-xl font-bold mb-4 text-yellow-300">🌊 BAY AREA EXPANSION</h4>
                <div className="space-y-2 text-blue-100">
                  <div>• Saint Petersburg POS</div>
                  <div>• Clearwater POS System</div>
                  <div>• Lakeland POS Terminal</div>
                  <div>• Plant City POS Services</div>
                  <div>• Riverview POS Solution</div>
                  <div>• Pinellas Park POS</div>
                </div>
              </div>
            </div>
          </div>

          {/* Service Areas Call-to-Action */}
          <div className="text-center bg-white rounded-2xl shadow-2xl p-8 border-4 border-orange-400">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              🎯 YOUR TAMPA BAY NEIGHBORHOOD IS COVERED!
            </h3>
            <p className="text-xl text-gray-700 mb-6">
              Whether you're in bustling Downtown Tampa, historic Ybor City, growing Brandon, or anywhere across the Bay Area - we deliver and install your FREE terminal the SAME DAY!
            </p>
            <div className="grid md:grid-cols-3 gap-4 mb-8 text-sm">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <div className="font-bold text-green-800">✅ SAME-DAY SERVICE</div>
                <div className="text-green-700">All Tampa Bay Areas</div>
              </div>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="font-bold text-blue-800">🚀 FREE INSTALLATION</div>
                <div className="text-blue-700">Professional Setup</div>
              </div>
              <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 space-y-1">
                <div className="font-bold text-orange-800">📞 LOCAL SUPPORT</div>
                <div className="text-orange-700">Tampa-Based Team</div>
                <div className="font-bold text-orange-800">🤝 Up to 24/7 Partnered Support</div>
              </div>
            </div>
            <button
              onClick={() => document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-12 py-6 rounded-xl text-2xl font-black transition-all duration-300 transform hover:scale-105 shadow-2xl animate-pulse"
            >
              📍 GET MY FREE TERMINAL IN MY TAMPA AREA
            </button>
          </div>
        </div>
      </section>

      {/* Pain Point Section */}
      <section className="py-16 bg-red-50 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-red-600 mb-4">
              TAMPA: STOP FUNDING SQUARE & CLOVER'S BILLION$ PROFITS!
            </h2>
            <p className="text-xl text-red-800 font-bold">
              Every Tampa business pays $30-$80 DAILY to enrich Silicon Valley billionaires
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                emoji: "😤",
                title: "Square's Hidden Fees",
                points: ["$60/month software fees", "$400+ terminal costs", "No cash discount options", "Terrible customer service"],
                cost: "YEARLY THEFT: $13,240+",
                color: "red"
              },
              {
                emoji: "🤬",
                title: "Clover's Trap",
                points: ["$100+/month software", "$600+ equipment costs", "Locked into contracts", "Overseas support only"],
                cost: "YEARLY ROBBERY: $14,080+",
                color: "red"
              },
              {
                emoji: "🚀",
                title: "MidaPOS Solution",
                points: ["$0 in processing fees", "FREE $1379 terminal", "Cash discount programs", "24/7 local Tampa support", "Support in English, Spanish, Hindi and Arabic"],
                cost: "YOUR PROFIT: $3,240 - 30,000+ / year",
                color: "green"
              }
            ].map((item, idx) => (
              <div key={idx} className={`${idx === 2 ? 'bg-gradient-to-r from-green-500 to-blue-600 text-white transform scale-105' : 'bg-white border-l-4 border-red-500'} rounded-xl shadow-lg p-8`}>
                <div className="text-center">
                  <div className="text-5xl mb-4">{item.emoji}</div>
                  <h3 className={`text-xl font-bold mb-4 ${idx === 2 ? 'text-white' : 'text-red-600'}`}>{item.title}</h3>
                  <div className={`space-y-2 ${idx === 2 ? 'text-white' : 'text-gray-700'}`}>
                    {item.points.map((point, i) => <p key={i}>• {point}</p>)}
                  </div>
                  <div className={`mt-4 p-3 rounded-lg ${idx === 2 ? 'bg-white bg-opacity-20' : 'bg-red-100'}`}>
                    <span className={`font-bold ${idx === 2 ? 'text-white' : 'text-red-800'}`}>{item.cost}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 px-4 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">1,247 Tampa Bay Businesses Eliminated ALL Processing Fees</h2>
            <div className="flex justify-center items-center space-x-2 mb-8">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-8 h-8 text-yellow-400 fill-current" />
              ))}
              <span className="text-2xl font-bold ml-4">4.97/5 Stars from Tampa Business Owners</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white text-gray-800 rounded-xl p-8 shadow-xl">
                <div className="flex items-center mb-4">
                  <div className="text-4xl mr-4">{index === 0 ? '🏪' : index === 1 ? '💨' : '👗'}</div>
                  <div>
                    <h4 className="font-bold text-lg">{testimonial.author}</h4>
                    <p className="text-gray-600">{testimonial.business}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.text}"</p>
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg font-bold text-center">
                  {testimonial.savings}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Urgency Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-red-600 text-white px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-black bg-opacity-20 rounded-xl p-8 backdrop-blur-sm">
            <h2 className="text-4xl font-black mb-6">⚠️ TAMPA ALERT: 12 FREE Terminals Left - Expires Soon!</h2>
            <div className="text-5xl font-black mb-6 animate-pulse">{formatTime(timeLeft)}</div>
            <p className="text-xl mb-8 font-bold">FINAL 12 terminals reserved for Tampa Bay area. Once run out, you'll pay $1359  full retail price.</p>

            <div className="grid grid-cols-2 gap-4 mb-8 text-lg">
              <div className="bg-white bg-opacity-20 p-4 rounded-lg">
                <div className="font-bold">What You Get FREE Today:</div>
                <div>✅ $0 for $1359 POS Terminal</div>
                <div>✅ $0 for $250 complete Installation</div>
                <div>✅ $0 for $100 value Complete Training</div>
                <div>✅ $0 for $175 inventory swap</div>
                <div className="text-green-300 font-bold">Total Value Today: $1,884 — You Pay $0</div>
              </div>
              <div className="bg-white bg-opacity-20 p-4 rounded-lg">
                <div className="font-bold">What You Pay Tomorrow:</div>
                <div>❌ $1359 for terminal</div>
                <div>❌ $250 installation</div>
                <div>❌ $100 training</div>
                <div>❌ $175 inventory swap</div>
                <div className="text-red-300 font-bold">Tomorrow Cost: $1,884</div>
              </div>
            </div>

            <button
              onClick={() => document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-red-600 px-12 py-6 rounded-xl text-2xl font-black hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-2xl animate-bounce"
            >
              🚨 RESERVE MY TAMPA TERMINAL NOW
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-black text-white px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-black mb-8">Ready to Join 1,247+ Tampa Businesses Making BANK?</h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-red-600 bg-opacity-30 p-6 rounded-xl border-2 border-red-500">
              <h3 className="text-2xl font-bold mb-4 text-red-300">❌ Stay a Square/Clover Victim</h3>
              <ul className="space-y-2 text-left">
                <li>• Waste $3,000-22,000/year</li>
                <li>• Deal with offshore support</li>
                <li>• Pay for expensive terminals</li>
                <li>• Stuck in long contracts</li>
              </ul>
            </div>

            <div className="bg-green-600 bg-opacity-30 p-6 rounded-xl border-2 border-green-500">
              <h3 className="text-2xl font-bold mb-4 text-green-300">✅ Join Tampa's Fee-Free Revolution</h3>
              <ul className="space-y-2 text-left">
                <li>• Save $3,000-$30,000/year</li>
                <li>• Get 24/7 local Tampa support</li>
                <li>• Support in English, Spanish, Hindi and Arabic</li>
                <li>• Receive FREE $1379 terminal</li>
                <li>• No contracts or commitments</li>
              </ul>
            </div>
          </div>

          <div className="space-y-6">
            <button
              onClick={() => document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-12 py-6 rounded-xl text-2xl font-black transition-all duration-300 transform hover:scale-105 shadow-2xl animate-pulse"
            >
              💰 YES! ELIMINATE MY FEES + CLAIM FREE TERMINAL
            </button>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="tel:3053067774" className="flex items-center space-x-2 bg-white bg-opacity-20 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-xl text-xl font-bold transition-all duration-300">
                <Phone className="w-6 h-6" />
                <span>TAMPA PRIORITY LINE: (305) 306-7774</span>
              </a>
              <div className="text-yellow-300 font-bold animate-pulse">⚡ TAMPA REP STANDING BY</div>
            </div>

            <div className="bg-green-600 bg-opacity-30 rounded-xl p-6 border border-green-400">
              <h3 className="text-xl font-bold mb-4 text-green-300">🛡️ IRON-CLAD TAMPA GUARANTEE</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>✅ 60-day money-back</div>
                <div>✅ Cancel anytime</div>
                <div>✅ No hidden fees EVER</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}